import ContentIllustration from '../../../components/ui/ContentIllustration';
import { useState, useMemo, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Eye, Users, User, KeyRound, Layers, X, Lock, FileDown, Search, Check } from 'lucide-react';
import ResultsExportModal from '../../../components/share/ResultsExportModal';
import CategoryCodeDetailModal from '../../../components/share/CategoryCodeDetailModal';
import api from '../../../services/api';
import QuizCodeReviewModal from '../../../components/custom/QuizCodeReviewModal';
import PlayModePicker from '../../../components/quiz/PlayModePicker';
import { scoreColor, avatarGradient, SORT_MODES, SORT_ORDER, groupHistory } from './resultats/helpers';
import HistoryView from './resultats/HistoryView';
import StatsView from './resultats/StatsView';
import ClearHistoryModal from './resultats/ClearHistoryModal';
import { CategoryCodesTracking, QuizCodesTracking } from './resultats/TrackingCodesTables';
import QrcHistoryView from './resultats/QrcHistoryView';
import NotionHistoryView from './resultats/NotionHistoryView';
import ResultsDock from '../../../components/ui/ResultsDock';

export default function TabResultats({
    scopeInfo, user, has_access = true, histLoading, histEntries, statsLoading, statsData,
    resultatsSub, setResultatsSub, startReview,
    setModalSousSujetId, setModalMode, setModalNbQuestions, setModalQuestionIds, setModalOrigin,
    onGoToQuiz, onHistoryCleared,
    showGenerations, renderGenerations,
    isOwnerThematique, thematiqueId,
}) {
    const [showClearModal, setShowClearModal] = useState(false);
    // "Rejouer" un audit : on demande d'abord le mode (Classique / Apprentissage).
    const [rejouerData, setRejouerData] = useState(null); // { allIds, sousSujetId }
    // « Refaire » dont une partie n'est plus accessible (abonnement expiré, questions retirées) :
    // { playable, total, locked, retired, launch } — on propose de rejouer la partie disponible.
    const [rejeuPartiel, setRejeuPartiel] = useState(null);
    // Dernière ligne de l'historique sur laquelle on a agi (Consulter / Refaire) : encadrée, comme le dernier sujet joué.
    const [activeHistKey, setActiveHistKey] = useState(null);
    const navigate = useNavigate();
    const [histFilter, setHistFilter] = useState('all'); // 'all' | 'classique' | 'apprentissage'
    const [histSort, setHistSort] = useState('recent'); // 'recent' (timeline/jour) | 'score' (classement faible→fort)
    const [trackingHistFilter, setTrackingHistFilter] = useState('all'); // suivi : filtre/tri indépendants
    const [trackingHistSort, setTrackingHistSort] = useState('recent');
    const [trackingSearch, setTrackingSearch] = useState(''); // recherche personne (suivi)
    const [trackingPersonFilter, setTrackingPersonFilter] = useState('all'); // 'all' | 'active' | 'inactive'
    const [trackingPersonSort, setTrackingPersonSort] = useState('score'); // cf. SORT_MODES
    const [copiedCode, setCopiedCode] = useState(null); // feedback copie code (suivi)

    // Suivi des partagés
    const [viewMode, setViewMode] = useState('mine'); // 'mine' | 'tracking'
    const [sharedUsers, setSharedUsers] = useState([]);
    const [sharedLoading, setSharedLoading] = useState(false);
    const [sharedLoaded, setSharedLoaded] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null); // { user_id, prenom, email }
    const [targetHist, setTargetHist] = useState([]);
    const [targetHistLoading, setTargetHistLoading] = useState(false);
    const [targetStats, setTargetStats] = useState(null);
    const [targetStatsLoading, setTargetStatsLoading] = useState(false);
    const [trackingSub, setTrackingSub] = useState('historique'); // 'historique' | 'stats'

    const [sharedLabels, setSharedLabels] = useState([]); // distinct labels for filter
    const [filterLabel, setFilterLabel] = useState(''); // '' = all, 'xxx' = filter by label, '__none__' = no label
    const [trackingMode, setTrackingMode] = useState('shared'); // 'shared' | 'category-codes' | 'quiz-codes'
    const [quizCodes, setQuizCodes] = useState([]);
    const [quizCodesLoading, setQuizCodesLoading] = useState(false);
    const [quizCodesLoaded, setQuizCodesLoaded] = useState(false);
    const [categoryCodes, setCategoryCodes] = useState([]);
    const [categoryCodesLoading, setCategoryCodesLoading] = useState(false);
    const [categoryCodesLoaded, setCategoryCodesLoaded] = useState(false);
    const [catDetailId, setCatDetailId] = useState(null);
    const [reviewQcId, setReviewQcId] = useState(null);
    // Export PDF — réutilise <ResultsExportModal> (même composant que « Gérer les partages »)
    const [codes, setCodes] = useState([]);
    const [codesLoaded, setCodesLoaded] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const [exportPreselect, setExportPreselect] = useState(null);

    const loadSharedUsers = useCallback(async () => {
        if (!thematiqueId || sharedLoaded) return;
        setSharedLoading(true);
        try {
            const { data } = await api.get('/share/user-stats.php', { params: { user_thematique_id: thematiqueId } });
            setSharedUsers(data.data?.users || []);
            setSharedLabels(data.data?.labels || []);
            setSharedLoaded(true);
        } catch {}
        setSharedLoading(false);
    }, [thematiqueId, sharedLoaded]);

    const loadQuizCodes = useCallback(async () => {
        if (!thematiqueId || quizCodesLoaded) return;
        setQuizCodesLoading(true);
        try {
            const { data } = await api.get('/quiz-code/list.php', { params: { user_thematique_id: thematiqueId } });
            setQuizCodes(data.data?.codes || []);
            setQuizCodesLoaded(true);
        } catch {}
        setQuizCodesLoading(false);
    }, [thematiqueId, quizCodesLoaded]);

    const loadCategoryCodes = useCallback(async () => {
        if (!thematiqueId || categoryCodesLoaded) return;
        setCategoryCodesLoading(true);
        try {
            const { data } = await api.get('/category-code/list.php', { params: { user_thematique_id: thematiqueId } });
            setCategoryCodes(data.data?.items || []);
            setCategoryCodesLoaded(true);
        } catch {}
        setCategoryCodesLoading(false);
    }, [thematiqueId, categoryCodesLoaded]);

    // Codes de partage bruts (pour l'export PDF). Renvoie la liste pour usage immédiat (évite le closure périmé).
    const loadShareCodes = useCallback(async () => {
        if (codesLoaded) return codes;
        if (!thematiqueId) return [];
        try {
            const { data } = await api.get(`/share/codes.php?user_thematique_id=${thematiqueId}`);
            const c = data.data?.codes || [];
            setCodes(c);
            setCodesLoaded(true);
            return c;
        } catch { return []; }
    }, [thematiqueId, codesLoaded, codes]);

    // Ouvre l'export global (charge les 3 types de codes d'abord).
    const handleOpenExport = async (preselect = null) => {
        await Promise.all([loadShareCodes(), loadCategoryCodes(), loadQuizCodes()]);
        setExportPreselect(preselect);
        setShowExport(true);
    };

    // Export rapide d'un seul participant : présélectionne ses codes de partage.
    const handleExportUser = async (su) => {
        const c = await loadShareCodes();
        loadCategoryCodes(); loadQuizCodes();
        const ids = c
            .filter(x => x.status === 'REDEEMED' && x.redeemed_by_email && su.email
                && x.redeemed_by_email.toLowerCase() === su.email.toLowerCase())
            .map(x => 'sc:' + x.id);
        setExportPreselect(ids.length ? new Set(ids) : null);
        setShowExport(true);
    };

    useEffect(() => {
        if (viewMode === 'tracking' && trackingMode === 'quiz-codes' && !quizCodesLoaded) loadQuizCodes();
        if (viewMode === 'tracking' && trackingMode === 'category-codes' && !categoryCodesLoaded) loadCategoryCodes();
    }, [viewMode, trackingMode, quizCodesLoaded, loadQuizCodes, categoryCodesLoaded, loadCategoryCodes]);

    useEffect(() => {
        if (viewMode === 'tracking' && !sharedLoaded) loadSharedUsers();
    }, [viewMode, sharedLoaded, loadSharedUsers]);

    const loadTargetHistory = useCallback(async (targetUserId) => {
        if (!scopeInfo?.id) return;
        setTargetHistLoading(true);
        try {
            const { data } = await api.get('/qcm/history.php', { params: { scope_id: scopeInfo.id, target_user_id: targetUserId } });
            setTargetHist(data.data || []);
        } catch { setTargetHist([]); }
        setTargetHistLoading(false);
    }, [scopeInfo?.id]);

    const loadTargetStats = useCallback(async (targetUserId) => {
        if (!scopeInfo?.id) return;
        setTargetStatsLoading(true);
        try {
            const { data } = await api.get('/user/stats.php', { params: { scope_id: scopeInfo.id, target_user_id: targetUserId } });
            setTargetStats(data.data || null);
        } catch { setTargetStats(null); }
        setTargetStatsLoading(false);
    }, [scopeInfo?.id]);

    const selectUser = (u) => {
        setSelectedUser(u);
        setTrackingSub('historique');
        loadTargetHistory(u.user_id);
        loadTargetStats(u.user_id);
    };
    const copyCode = (code) => {
        try {
            navigator.clipboard.writeText(code);
            setCopiedCode(code);
            setTimeout(() => setCopiedCode(c => (c === code ? null : c)), 1500);
        } catch { /* clipboard indisponible */ }
    };

    // Sceller / desceller les résultats d'un code (mode examen). Le rechargement
    // effectif passe par le useEffect ci-dessus (loaded=false → reload).
    const toggleCatSeal = async (c) => {
        const action = c.review_released_at ? 'seal_review' : 'release_review';
        try { await api.post('/category-code/list.php', { action, code_id: c.id }); setCategoryCodesLoaded(false); loadCategoryCodes(); }
        catch (err) { alert(err.response?.data?.message || 'Erreur'); }
    };
    const toggleQuizSeal = async (c) => {
        const action = c.review_released_at ? 'seal_review' : 'release_review';
        try { await api.post('/quiz-code/list.php', { action, code_id: c.id }); setQuizCodesLoaded(false); loadQuizCodes(); }
        catch (err) { alert(err.response?.data?.message || 'Erreur'); }
    };

    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const toggleGroup = (g) => setExpandedGroups(prev => {
        const next = new Set(prev);
        next.has(g) ? next.delete(g) : next.add(g);
        return next;
    });

    const groupedHistory = useMemo(() => groupHistory(histEntries), [histEntries]);

    const doConsulter = async (it) => {
        if (it.kind === 'single') {
            const e = it.entry;
            await startReview(e, scopeInfo?.id, { isCustom: scopeInfo?.content_source === 'user', isPartner: scopeInfo?.content_source === 'partner' });
            setModalSousSujetId(e.sous_sujet_id); setModalMode(null); setModalNbQuestions(null); setModalQuestionIds(null); setModalOrigin('subject');
        } else {
            const g = it.group;
            const allIds = g.children.flatMap(c => c.question_ids || []);
            const merged = { id: g.children[0].id, historyIds: g.children.map(c => c.id), sous_sujet_id: g.children[0].sous_sujet_id, scope_id: g.children[0].scope_id, question_ids: allIds, origin: g.origin, created_at: g.children[0].created_at };
            await startReview(merged, scopeInfo?.id, { isCustom: scopeInfo?.content_source === 'user', isPartner: scopeInfo?.content_source === 'partner' });
            setModalSousSujetId(merged.sous_sujet_id); setModalMode(null); setModalNbQuestions(null); setModalQuestionIds(null); setModalOrigin('subject');
        }
    };
    // Avant de rejouer une séance de l'historique : questions.php refuse la séance entière si une seule
    // question sort du périmètre (Mélange mêlant sujets gratuits et payants après expiration, 27/10/2026).
    // On rejoue alors seulement la partie jouable, en le disant.
    const verifierRejeu = async (ids, launch) => {
        try {
            const { data } = await api.post('/qcm/replay-scope.php', { scope_id: scopeInfo?.id, question_ids: ids });
            const r = data.data;
            if (!r.locked && !r.retired) return launch(ids, null);
            setRejeuPartiel({ ...r, launch });
        } catch (err) {
            if (err?.response?.status === 403) setRejeuPartiel({ playable: [], total: ids.length, locked: ids.length, retired: 0, launch });
            else launch(ids, null); // vérification indisponible : comportement d'avant
        }
    };
    const doRefaire = (it) => {
        if (it.kind === 'single') {
            const e = it.entry;
            verifierRejeu(e.question_ids || [], (ids, anchor) => {
                setModalSousSujetId(anchor ?? e.sous_sujet_id); setModalMode(e.mode || 'quiz'); setModalNbQuestions(null); setModalQuestionIds(ids); setModalOrigin(`history:${e.id}`);
            });
        } else {
            const g = it.group;
            const allIds = g.children.flatMap(c => c.question_ids || []);
            if ((g.origin === 'audit-parcours' || g.origin === 'audit-diagnostic')) {
                // Audit : "Rejouer" → choix du mode, rejoué en mélange (pas un nouvel audit).
                verifierRejeu(allIds, (ids, anchor) => setRejouerData({ allIds: ids, sousSujetId: anchor ?? g.children[0].sous_sujet_id }));
            } else {
                verifierRejeu(allIds, (ids, anchor) => {
                    setModalSousSujetId(anchor ?? g.children[0].sous_sujet_id); setModalMode(g.mode || 'quiz'); setModalNbQuestions(null); setModalQuestionIds(ids); setModalOrigin(g.origin || 'melange');
                });
            }
        }
    };

    const renderHistorique = () => {
        if (!user) return <p className="sl-login-prompt">Connectez-vous pour voir votre historique.</p>;
        if (histLoading) return <div className="loading">Chargement...</div>;
        if (histEntries.length === 0) return (
            <div className="sl-empty-state">
                <span className="sl-empty-icon"><ContentIllustration name="clipboard-list" size={64} /></span>
                <p>Aucun quiz terminé pour le moment.</p>
                <button className="btn btn-primary btn-sm" onClick={onGoToQuiz}>Commencer un quiz</button>
            </div>
        );
        return (
            <HistoryView
                entries={histEntries}
                filter={histFilter} setFilter={setHistFilter} sort={histSort} setSort={setHistSort}
                readOnly={false}
                onClear={onHistoryCleared ? () => setShowClearModal(true) : null}
                expandedGroups={expandedGroups} toggleGroup={toggleGroup}
                onConsulter={(it) => { setActiveHistKey(it.key); doConsulter(it); }} onRefaire={(it) => { setActiveHistKey(it.key); doRefaire(it); }} activeKey={activeHistKey}
            />
        );
    };

    const renderStats = () => {
        if (!user) return <p className="sl-login-prompt">Connectez-vous pour voir vos statistiques.</p>;
        if (statsLoading) return <div className="loading">Chargement...</div>;
        if (!statsData) return <div className="sl-empty-state"><span className="sl-empty-icon"><BarChart3 size={40} strokeWidth={1.5} /></span><p>Aucune donnee disponible.</p></div>;
        return <StatsView stats={statsData} />;
    };

    if (!user) return <p className="sl-login-prompt">Connectez-vous pour voir vos résultats.</p>;

    // Donut de score d'un participant (vide/gris si jamais commencé).
    const renderDonut = (pct) => {
        const C = 2 * Math.PI * 33;
        const has = pct != null;
        const col = has ? scoreColor(pct) : '#cbd5e1';
        return (
            <svg width="50" height="50" viewBox="0 0 80 80" style={{ flex: 'none' }}>
                <circle cx="40" cy="40" r="33" fill="none" stroke={has ? `${col}33` : '#eef1f7'} strokeWidth="7" />
                <circle cx="40" cy="40" r="33" fill="none" stroke={col} strokeWidth="7" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={has ? C * (1 - pct / 100) : C} transform="rotate(-90 40 40)" />
                <text x="40" y="46" textAnchor="middle" fontFamily="var(--font-display)" fontWeight="800" fontSize={has ? 17 : 15} fill={col}>{has ? `${pct}%` : '—'}</text>
            </svg>
        );
    };

    const renderSharedUsersList = () => {
        if (sharedLoading) return <div className="loading">Chargement...</div>;
        if (sharedUsers.length === 0) return (
            <div className="sl-empty-state">
                <span className="sl-empty-icon"><ContentIllustration name="users" size={64} /></span>
                <p>Aucun utilisateur n'a encore utilis&eacute; vos codes de partage.</p>
            </div>
        );
        // Résumé cohorte (sur l'ensemble, hors filtres).
        const nbPersonnes = sharedUsers.length;
        const nbActives = sharedUsers.filter(u => (u.quiz_completed || 0) > 0).length;
        const rated = sharedUsers.filter(u => u.success_rate != null);
        const scoreMoyen = rated.length ? Math.round(rated.reduce((s, u) => s + u.success_rate, 0) / rated.length) : 0;
        const nbQuiz = sharedUsers.reduce((s, u) => s + (u.quiz_completed || 0), 0);

        // Filtres (statut + groupe + recherche) puis tri.
        const q = trackingSearch.trim().toLowerCase();
        const list = sharedUsers.filter(u => {
            const engaged = (u.quiz_completed || 0) > 0;
            if (trackingPersonFilter === 'active' && !engaged) return false;
            if (trackingPersonFilter === 'inactive' && engaged) return false;
            if (filterLabel === '__none__' && u.label) return false;
            if (filterLabel && filterLabel !== '__none__' && u.label !== filterLabel) return false;
            if (q && !((u.prenom || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))) return false;
            return true;
        }).sort(SORT_MODES[trackingPersonSort].cmp);
        const sort = SORT_MODES[trackingPersonSort];

        return (
            <>
                {/* Résumé cohorte */}
                <div className="track-kpis">
                    <div className="stats-card"><div className="stats-card__num">{nbPersonnes}</div><div className="stats-card__lbl">Personnes</div></div>
                    <div className="stats-card"><div className="stats-card__num" style={{ color: '#10b981' }}>{nbActives}</div><div className="stats-card__lbl">Avec activité</div></div>
                    <div className="stats-card"><div className="stats-card__num">{scoreMoyen}%</div><div className="stats-card__lbl">Score moyen</div></div>
                    <div className="stats-card"><div className="stats-card__num">{nbQuiz}</div><div className="stats-card__lbl">Quiz réalisés</div></div>
                </div>

                {/* Filtres statut + tri */}
                <div className="track-filters-row">
                    <div className="hist-filters">
                        <button type="button" className={`hist-filter ${trackingPersonFilter === 'all' ? 'is-active' : ''}`} onClick={() => setTrackingPersonFilter('all')}>Tous ({nbPersonnes})</button>
                        <button type="button" className={`hist-filter ${trackingPersonFilter === 'active' ? 'is-active' : ''}`} onClick={() => setTrackingPersonFilter('active')}>Activité débutée ({nbActives})</button>
                        <button type="button" className={`hist-filter ${trackingPersonFilter === 'inactive' ? 'is-active' : ''}`} onClick={() => setTrackingPersonFilter('inactive')}>Sans activité ({nbPersonnes - nbActives})</button>
                    </div>
                    <button type="button" className="track-sort" onClick={() => setTrackingPersonSort(s => SORT_ORDER[(SORT_ORDER.indexOf(s) + 1) % SORT_ORDER.length])} title="Changer le tri">
                        Trier : <b>{sort.label} {sort.arrow}</b>
                    </button>
                </div>

                {/* Filtre groupe (cohortes formateur) */}
                {sharedLabels.length > 0 && (
                    <div className="track-groups">
                        <span className="track-groups__lbl">Groupe :</span>
                        <button type="button" className={`hist-filter ${!filterLabel ? 'is-active' : ''}`} onClick={() => setFilterLabel('')}>Tous</button>
                        {sharedLabels.map(l => (
                            <button key={l} type="button" className={`hist-filter ${filterLabel === l ? 'is-active' : ''}`} onClick={() => setFilterLabel(filterLabel === l ? '' : l)}>{l}</button>
                        ))}
                        {sharedUsers.some(u => !u.label) && (
                            <button type="button" className={`hist-filter ${filterLabel === '__none__' ? 'is-active' : ''}`} onClick={() => setFilterLabel(filterLabel === '__none__' ? '' : '__none__')}>Sans groupe</button>
                        )}
                    </div>
                )}

                {/* Cartes personnes */}
                <div className="track-list">
                    {list.length === 0 ? (
                        <p className="sl-empty" style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)' }}>Aucune personne ne correspond.</p>
                    ) : list.map((su) => {
                        const engaged = (su.quiz_completed || 0) > 0;
                        const isOpen = selectedUser && selectedUser.user_id === su.user_id;
                        const name = su.prenom || su.email;
                        const initial = (name || '?').trim().charAt(0).toUpperCase();
                        const dureeLabel = su.duree === '1m' ? '1 mois' : su.duree === '2m' ? '2 mois' : su.duree === '6m' ? '6 mois' : '12 mois';
                        const lastActDate = su.last_activity ? new Date(su.last_activity) : null;
                        const isToday = lastActDate && lastActDate.toDateString() === new Date().toDateString();
                        const lastAct = lastActDate ? `dernière activité ${isToday ? "aujourd'hui" : lastActDate.toLocaleDateString('fr-FR')}` : 'jamais commencé';
                        return (
                            <Fragment key={su.user_id}>
                                <div className={`track-card ${!engaged ? 'track-card--inactive' : ''} ${isOpen ? 'track-card--open' : ''}`}
                                     onClick={() => isOpen ? setSelectedUser(null) : selectUser(su)}>
                                    {renderDonut(su.success_rate)}
                                    <span className="track-card__avatar" style={{ background: engaged ? avatarGradient(name) : '#e2e8f0', color: engaged ? '#fff' : '#94a3b8' }}>{initial}</span>
                                    <div className="track-card__info">
                                        <div className="track-card__nameline">
                                            <span className="track-card__name">{name}</span>
                                            <span className={`track-badge ${engaged ? 'track-badge--active' : 'track-badge--inactive'}`}>{engaged ? 'Activité débutée' : 'Sans activité'}</span>
                                            {su.label && <span className="track-card__group">{su.label}</span>}
                                            {!su.is_active && <span className="track-card__expired">accès expiré</span>}
                                        </div>
                                        <div className="track-card__email">{su.email}</div>
                                        <div className="track-card__meta">Accès {dureeLabel} · {lastAct}</div>
                                    </div>
                                    <div className="track-card__quiz">
                                        <div className="track-card__quiz-n">{su.quiz_completed} quiz</div>
                                        {engaged && <div className="track-card__quiz-sub">{su.total_answers} réponses</div>}
                                    </div>
                                    <button type="button" className="track-card__btn" onClick={e => { e.stopPropagation(); isOpen ? setSelectedUser(null) : selectUser(su); }}>
                                        {isOpen ? <><X size={14} /> Fermer</> : <><Eye size={14} /> Voir</>}
                                    </button>
                                </div>
                                {isOpen && (
                                    <div className="sl-tracking-inline" style={{ margin: '-2px 0 12px', padding: '14px 16px', border: '1px solid var(--card-border)', borderRadius: 12, background: 'rgba(148, 163, 184, 0.06)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 8 }}>
                                            <button className="btn btn-xs btn-ghost" onClick={() => handleExportUser(su)} title="Exporter le PDF de ce participant"><FileDown size={14} /></button>
                                            <button className="btn btn-xs btn-ghost" onClick={() => setSelectedUser(null)} title="Fermer"><X size={16} /></button>
                                        </div>
                                        <div className="hist-switch">
                                            <button type="button" className={`hist-switch__card ${trackingSub === 'historique' ? 'is-active' : ''}`} onClick={() => setTrackingSub('historique')}>
                                                <span className="hist-switch__num">01</span>
                                                <span className="hist-switch__body">
                                                    <span className="hist-switch__title">Historique</span>
                                                    <span className="hist-switch__sub">{groupHistory(targetHist).length} quiz termin&eacute;{groupHistory(targetHist).length > 1 ? 's' : ''}</span>
                                                </span>
                                            </button>
                                            <button type="button" className={`hist-switch__card ${trackingSub === 'stats' ? 'is-active' : ''}`} onClick={() => setTrackingSub('stats')}>
                                                <span className="hist-switch__num">02</span>
                                                <span className="hist-switch__body">
                                                    <span className="hist-switch__title">Statistiques</span>
                                                    <span className="hist-switch__sub">Score &amp; points faibles</span>
                                                </span>
                                            </button>
                                        </div>
                                        {trackingSub === 'historique' && renderTargetHistory()}
                                        {trackingSub === 'stats' && renderTargetStats()}
                                    </div>
                                )}
                            </Fragment>
                        );
                    })}
                </div>
            </>
        );
    };

    const renderTargetHistory = () => {
        if (targetHistLoading) return <div className="loading">Chargement...</div>;
        if (targetHist.length === 0) return (
            <div className="sl-empty-state">
                <span className="sl-empty-icon"><ContentIllustration name="clipboard-list" size={64} /></span>
                <p>Aucun quiz termin&eacute; pour le moment.</p>
            </div>
        );
        return (
            <HistoryView
                entries={targetHist}
                filter={trackingHistFilter} setFilter={setTrackingHistFilter}
                sort={trackingHistSort} setSort={setTrackingHistSort}
                readOnly={true} onClear={null}
                expandedGroups={expandedGroups} toggleGroup={toggleGroup}
                onConsulter={(it) => { setActiveHistKey(it.key); doConsulter(it); }} onRefaire={(it) => { setActiveHistKey(it.key); doRefaire(it); }} activeKey={activeHistKey}
            />
        );
    };

    const renderTargetStats = () => {
        if (targetStatsLoading) return <div className="loading">Chargement...</div>;
        if (!targetStats) return <div className="sl-empty-state"><span className="sl-empty-icon"><BarChart3 size={40} strokeWidth={1.5} /></span><p>Aucune donn&eacute;e disponible.</p></div>;
        return <StatsView stats={targetStats} />;
    };

    return (
        <div className="sl-resultats" style={{ '--hist-accent': scopeInfo?.couleur_theme || '#7c3aed' }}>
            {/* Switch Vos résultats / Suivi */}
            {isOwnerThematique && (
                <div className="view-switch">
                    <button type="button" className={`view-switch__card ${viewMode === 'mine' ? 'is-active' : ''}`} onClick={() => { setViewMode('mine'); setSelectedUser(null); }}>
                        <span className="view-switch__icon"><User size={16} /></span>
                        <span className="view-switch__body">
                            <span className="view-switch__title">Tes r&eacute;sultats</span>
                            <span className="view-switch__sub">Ton historique &amp; tes stats</span>
                        </span>
                        <span className="view-switch__radio">{viewMode === 'mine' && <Check size={11} />}</span>
                    </button>
                    <button type="button" className={`view-switch__card ${viewMode === 'tracking' ? 'is-active' : ''}`} onClick={() => setViewMode('tracking')}>
                        <span className="view-switch__icon"><ContentIllustration name="users" size={16} /></span>
                        <span className="view-switch__body">
                            <span className="view-switch__title">Suivi des r&eacute;sultats</span>
                            <span className="view-switch__sub">La progression des participants</span>
                        </span>
                        <span className="view-switch__radio">{viewMode === 'tracking' && <Check size={11} />}</span>
                    </button>
                </div>
            )}

            {/* Mode: Vos résultats (original) */}
            {viewMode === 'mine' && !has_access && (
                <div className="sl-locked-view">
                    <div className="sl-locked-view__icon"><Lock size={40} /></div>
                    <h3 className="sl-locked-view__title">Accès à vos résultats verrouillé</h3>
                    <p className="sl-locked-view__desc">
                        Renouvelez votre abonnement pour consulter votre historique de quiz et vos statistiques de progression.
                    </p>
                    <p className="sl-locked-view__hint">
                        Utilisez le bouton <strong>Renouveler</strong> en haut à droite de la thématique.
                    </p>
                </div>
            )}
            {/* Une notion achetée l'est indépendamment de l'abonnement : ses séances restent consultables. */}
            {viewMode === 'mine' && !has_access && user && scopeInfo?.content_source !== 'user' && (
                <NotionHistoryView scopeInfo={scopeInfo} reloadKey={histEntries} />
            )}
            {viewMode === 'mine' && has_access && <>
                <ResultsDock
                    activeView={resultatsSub}
                    onViewChange={setResultatsSub}
                    accentColor={scopeInfo?.couleur_theme}
                    sessionCount={groupedHistory.length}
                    statsData={statsData}
                />
                <div className="results-dock__content">
                    {resultatsSub === 'historique' && renderHistorique()}
                    {/* Réponses rédigées (QRC) : section masquée tant qu'aucune correction n'existe dans ce scope.
                        Rechargée avec l'historique QCM (même déclencheurs : fin de quiz, effacement). */}
                    {resultatsSub === 'historique' && (
                        <QrcHistoryView scopeId={scopeInfo?.id} scopeColor={scopeInfo?.couleur_theme} reloadKey={histEntries} />
                    )}
                    {/* Notions : leurs séances tournent dans la thématique technique « notions », hors de l'historique ci-dessus. */}
                    {resultatsSub === 'historique' && scopeInfo?.content_source !== 'user' && (
                        <NotionHistoryView scopeInfo={scopeInfo} reloadKey={histEntries} />
                    )}
                    {resultatsSub === 'stats' && renderStats()}
                </div>
                {/* Si l'utilisateur a un localStorage "generations", on retombe sur historique automatiquement */}
                {resultatsSub === 'generations' && (() => {
                    setResultatsSub('historique');
                    return null;
                })()}
            </>}

            {/* Mode: Suivi des résultats */}
            {viewMode === 'tracking' && <>
                <div className="sl-examen-header" style={{ marginBottom: 16 }}>
                    <h3 className="sl-section-title">Suivi des r&eacute;sultats</h3>
                    <p className="sl-section-sub">
                        Suis la progression des personnes &agrave; qui tu as partag&eacute; ta th&eacute;matique.
                    </p>
                    <div className="track-toolbar">
                        <div className="track-seg">
                            {[
                                { k: 'shared', icon: Users, label: 'Par personne', sub: 'code partagé' },
                                { k: 'category-codes', icon: Layers, label: 'Par catégorie', sub: 'code catégorie' },
                                { k: 'quiz-codes', icon: KeyRound, label: 'Par quiz', sub: 'code quiz' },
                            ].map(({ k, icon: Icon, label, sub }) => (
                                <button key={k} type="button" className={`track-seg__btn ${trackingMode === k ? 'is-active' : ''}`} onClick={() => setTrackingMode(k)}>
                                    <span className="track-seg__icon"><Icon size={15} /></span>
                                    <span className="track-seg__txt"><span className="track-seg__label">{label}</span><span className="track-seg__sub">{sub}</span></span>
                                </button>
                            ))}
                        </div>
                        {trackingMode === 'shared' && (
                            <div className="track-search">
                                <Search size={15} />
                                <input type="text" placeholder="Rechercher…" value={trackingSearch} onChange={e => setTrackingSearch(e.target.value)} aria-label="Rechercher une personne" />
                                {trackingSearch && <button type="button" onClick={() => setTrackingSearch('')} aria-label="Effacer"><X size={13} /></button>}
                            </div>
                        )}
                        <button type="button" className="track-export" onClick={() => handleOpenExport()}
                            title="Exporter les résultats des participants (attestation PDF Qualiopi)">
                            <FileDown size={15} /> Exporter PDF
                        </button>
                    </div>
                </div>
                {trackingMode === 'shared' && renderSharedUsersList()}
                {trackingMode === 'category-codes' && (
                    <CategoryCodesTracking
                        loading={categoryCodesLoading} codes={categoryCodes}
                        copiedCode={copiedCode} copyCode={copyCode}
                        onDetail={setCatDetailId} onToggleSeal={toggleCatSeal}
                    />
                )}
                {trackingMode === 'quiz-codes' && (
                    <QuizCodesTracking
                        loading={quizCodesLoading} codes={quizCodes}
                        copiedCode={copiedCode} copyCode={copyCode}
                        onReview={setReviewQcId} onToggleSeal={toggleQuizSeal}
                    />
                )}
            </>}

            {/* Détail d'un utilisateur : affiché désormais INLINE sous sa ligne (voir renderSharedUsersList). */}

            {showClearModal && (
                <ClearHistoryModal
                    scopeId={scopeInfo.id}
                    onClose={() => setShowClearModal(false)}
                    onCleared={onHistoryCleared}
                />
            )}

            {/* Detail modal for a category-code participant */}
            <ResultsExportModal
                open={showExport}
                onClose={() => { setShowExport(false); setExportPreselect(null); }}
                thematiqueId={thematiqueId}
                codes={codes}
                categoryCodes={categoryCodes}
                quizCodes={quizCodes}
                userStats={sharedUsers}
                initialSelectedIds={exportPreselect}
            />
            {catDetailId && (
                <CategoryCodeDetailModal
                    item={categoryCodes.find(c => c.id === catDetailId)}
                    onClose={() => setCatDetailId(null)}
                    onReviewChild={(ch) => setReviewQcId(ch.id)}
                />
            )}

            {reviewQcId && (
                <QuizCodeReviewModal
                    quizCodeId={reviewQcId}
                    onClose={() => setReviewQcId(null)}
                />
            )}

            {/* Refaire partiel : une partie de la séance n'est plus accessible */}
            {rejeuPartiel && (() => {
                const { playable, total, locked, retired, launch } = rejeuPartiel;
                const n = playable.length;
                const abonnable = locked > 0 && scopeInfo?.slug && scopeInfo?.content_source !== 'user';
                const fermer = () => setRejeuPartiel(null);
                return (
                    <div className="play-mode-overlay" onClick={(e) => e.target === e.currentTarget && fermer()}>
                        <div className="play-mode-modal">
                            <h3 className="play-mode-title">{n > 0 ? 'Rejouer la partie disponible' : 'Séance non disponible'}</h3>
                            <p style={{ fontSize: '0.9em', color: 'var(--text-muted)', margin: '0 0 16px', textAlign: 'center', lineHeight: 1.5 }}>
                                {n > 0
                                    ? <><strong style={{ color: 'var(--text)' }}>{n} question{n > 1 ? 's' : ''} sur {total}</strong> {n > 1 ? 'sont rejouables' : 'est rejouable'}{locked > 0 ? ' sans abonnement' : ''}.</>
                                    : <>Aucune question de cette séance n'est rejouable pour le moment.</>}
                                {locked > 0 && <> {locked} {locked > 1 ? 'font' : 'fait'} partie de l'abonnement de la thématique.</>}
                                {retired > 0 && <> {retired} {retired > 1 ? 'ne sont plus proposées' : "n'est plus proposée"}.</>}
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {n > 0 && (
                                    <button type="button" className="btn btn-primary" onClick={() => { fermer(); launch(playable, rejeuPartiel.anchor_sous_sujet_id); }}>
                                        Rejouer {n > 1 ? `les ${n} questions` : 'la question'}
                                    </button>
                                )}
                                {abonnable && (
                                    <button type="button" className={n > 0 ? 'btn btn-outline' : 'btn btn-primary'} onClick={() => { fermer(); navigate(`/compte/quiz/${scopeInfo.slug}/abonnement`); }}>
                                        <Lock size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />Voir les abonnements pour tout rejouer
                                    </button>
                                )}
                            </div>
                            <button className="play-mode-cancel" onClick={fermer}>{n > 0 ? 'Annuler' : 'Fermer'}</button>
                        </div>
                    </div>
                );
            })()}

            {/* Rejouer un audit : choix du mode, puis relance en mélange */}
            {rejouerData && (
                <PlayModePicker
                    title="Rejouer l'audit en quiz mélangé"
                    onSelect={(playMode) => {
                        setModalSousSujetId(rejouerData.sousSujetId);
                        setModalMode(playMode);
                        setModalNbQuestions(null);
                        setModalQuestionIds(rejouerData.allIds);
                        setModalOrigin('melange');
                        setRejouerData(null);
                    }}
                    onCancel={() => setRejouerData(null)}
                />
            )}
        </div>
    );
}
